import { DestroyRef, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
    providedIn: 'root',
})
export class GlobalCommandPaletteService {
    private readonly destroyRef = inject(DestroyRef);
    private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

    readonly isOpen = signal(false);

    private triggerElement: HTMLElement | null = null;
    private previousActiveElement: HTMLElement | null = null;

    constructor() {
        if (!this.isBrowser) {
            return;
        }

        const onKeydown = (event: KeyboardEvent) => this.onDocumentKeydown(event);
        document.addEventListener('keydown', onKeydown);
        this.destroyRef.onDestroy(() => document.removeEventListener('keydown', onKeydown));
    }

    open(trigger?: HTMLElement | null): void {
        if (trigger) {
            this.triggerElement = trigger;
        }

        if (this.isBrowser) {
            this.previousActiveElement = document.activeElement as HTMLElement | null;
            document.body.classList.add('overflow-hidden');
        }

        this.isOpen.set(true);
    }

    close(): void {
        this.isOpen.set(false);

        if (!this.isBrowser) {
            return;
        }

        document.body.classList.remove('overflow-hidden');
        const restoreTarget = this.triggerElement ?? this.previousActiveElement;
        restoreTarget?.focus();
    }

    toggle(trigger?: HTMLElement | null): void {
        if (this.isOpen()) {
            this.close();
            return;
        }

        this.open(trigger);
    }

    private onDocumentKeydown(event: KeyboardEvent): void {
        const key = event.key.toLowerCase();

        if ((event.ctrlKey || event.metaKey) && key === 'k') {
            if (!this.isOpen()) {
                event.preventDefault();
                this.open();
            }
            return;
        }

        if (key === 'escape' && this.isOpen()) {
            event.preventDefault();
            this.close();
        }
    }
}
